/**
 * Config-change log screen — the Configuration-group, newest-first history of
 * per-tenant configuration writes (connections, plans, matrices, caps, bands,
 * classifications, writable-fields, settings, tenant), backed by
 * `GET /admin/config-audit` (DNKADM-0024 / DNKAPI-0077). It is the config
 * counterpart to the Operations Activity log and never double-reports it:
 * member-state actions live in the Activity log; only configuration writes appear
 * here. RequireAdmin-gated (Configuration surface).
 *
 * Each row: the actor (WHO) · timestamp (WHEN) meta line, an entity chip
 * (`entityType` + `entityKey`), an action chip (create/update/delete, or a raw
 * unknown action), and a before→after rendering of the changed values —
 *  - CREATE shows `newValue` (the created config);
 *  - DELETE shows `oldValue` (the removed config);
 *  - UPDATE shows a key-by-key `old → new` of ONLY the differing keys.
 *
 * SECRETS: the server masks secret values to last-4 inside `oldValue`/`newValue`
 * (e.g. `access_token: "****wxyz"`). This screen RENDERS those values VERBATIM as
 * plain text — never an input, never re-fetched or treated as a live secret, and
 * with NO client-side masking (the server already masks). It is READ-ONLY: it
 * displays config changes, it never edits or reverts.
 *
 * States: loading (skeleton rows), empty (loaded, no entries), error (with a
 * dedicated 403 "needs OWNER/ADMIN" notice), and loaded — the established
 * `useResource` pattern. Paging drives the server `page` param (prev/next), the
 * same convention as the Activity log. An entity-type filter drives the server
 * `?entityType=` param. The feed re-sorts newest-first client-side defensively.
 *
 * Toolbar and URL state (DNKADM-0430 P1-C): the shared `ListToolbar` carries the Entity
 * type filter and a From/To date range (the server's existing `?fromDate=`/`?toDate=`),
 * and both plus the page live in the URL, so a link or a reload reproduces the view. The
 * feed is server paged, so it has no sort control until server sort lands (DNKAPI-0615).
 */
import { useCallback } from 'react';
import { Box, Chip, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { ListSelectFilter, ListToolbar } from '@/components/list/ListToolbar';
import { parseDateParam } from '@/components/list/listDate';
import {
  parseOption,
  parsePage,
  toPageParam,
  useListUrlState,
} from '@/components/list/useListUrlState';
import { Icon, MONO, inkAlpha, resolveTokens } from '@/theme';
import { buildConfigAuditMaps } from '@/theme/maps/configAudit';
import { useResource } from '@/hooks/useResource';
import {
  listConfigAudit,
  CONFIG_ENTITY_TYPES,
  type ConfigAuditEntry,
  type ConfigValue,
} from '@/api/configAudit';
import { entityTypeLabel } from './configEntity';
import { roleLimitedMessage } from '@/components/accessCopy';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_TIME, formatInstant } from '@/utils/instantFormat';

const DEFAULT_PAGE_LIMIT = 20;
/** Skeleton rows shown on first load. */
const SKELETON_ROWS = 6;
/** The "all entity types" filter value (the shared select's "All ..." option); omits `?entityType=`. */
const ALL_TYPES = '';
/**
 * The screen's URL params (DNKADM-0430 P1-C), all optional: `entity` (one stored entity
 * type, `?entityType=` on the wire), `from` / `to` (`YYYY-MM-DD`, `?fromDate=` /
 * `?toDate=`), and the 1-indexed `page`. The server also accepts `?actor=`, an exact match
 * on the stored actor value; it is not exposed (see the DNKADM-0430 P1-C delivery).
 */
const CONFIG_AUDIT_URL_DEFAULTS = { entity: '', from: '', to: '', page: '1' };

/**
 * Format an ISO/epoch timestamp for display (e.g. "Jun 10, 9:12 AM"). Falls back
 * to the raw string when it isn't parseable, and to a placeholder when absent —
 * never throws. (Mirrors the Activity log's `formatTime`.)
 */
function formatTime(ts: string | null, timeZone: string | null): string {
  // On the venue's clock (DNKADM-0476 F-05), not the browser's.
  return formatInstant(ts, timeZone, DATE_TIME);
}

/**
 * Stringify a config value for display — VERBATIM, never unmasked. A string
 * passes through unchanged (so a server-masked `"****wxyz"` shows exactly that);
 * other primitives stringify; objects/arrays render as compact JSON. `null`/
 * `undefined` show an em dash.
 */
function displayValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** A single field change in a before→after rendering. */
interface FieldChange {
  key: string;
  /** The pre-write value (null = field absent before, e.g. on create). */
  oldText: string | null;
  /** The post-write value (null = field absent after, e.g. on delete). */
  newText: string | null;
}

/**
 * Compute the field-level before→after rows for an entry:
 *  - CREATE → every key of `newValue` (old absent).
 *  - DELETE → every key of `oldValue` (new absent).
 *  - UPDATE → only the keys whose value DIFFERS between old and new.
 * Values are compared by their display string (stable for the masked/JSON forms
 * we render), so an unchanged masked secret is correctly treated as unchanged.
 */
function diffFields(entry: ConfigAuditEntry): FieldChange[] {
  const oldV = entry.oldValue;
  const newV = entry.newValue;

  // Create: no old snapshot → show the created fields.
  if (oldV === null && newV !== null) {
    return Object.keys(newV).map((key) => ({
      key,
      oldText: null,
      newText: displayValue(newV[key]),
    }));
  }
  // Delete: no new snapshot → show the removed fields.
  if (newV === null && oldV !== null) {
    return Object.keys(oldV).map((key) => ({
      key,
      oldText: displayValue(oldV[key]),
      newText: null,
    }));
  }
  // Update: show only the differing keys (old → new).
  if (oldV !== null && newV !== null) {
    return changedKeys(oldV, newV).map((key) => ({
      key,
      oldText: key in oldV ? displayValue(oldV[key]) : null,
      newText: key in newV ? displayValue(newV[key]) : null,
    }));
  }
  return [];
}

/** Union of keys whose display value differs between the two snapshots, in old-then-new order. */
function changedKeys(oldV: ConfigValue, newV: ConfigValue): string[] {
  const keys = new Set<string>([...Object.keys(oldV), ...Object.keys(newV)]);
  const out: string[] = [];
  for (const key of keys) {
    const before = key in oldV ? displayValue(oldV[key]) : null;
    const after = key in newV ? displayValue(newV[key]) : null;
    if (before !== after) out.push(key);
  }
  return out;
}

export function ConfigAuditLog() {
  // The view state lives in the URL (DNKADM-0430 P1-C): entity type, date range and page,
  // so a copied link or a reload reproduces the view. Writes replace the history entry, so
  // Back leaves the screen. Every raw param is validated; a bad one reads as unfiltered.
  const url = useListUrlState(CONFIG_AUDIT_URL_DEFAULTS);
  const setUrl = url.set;
  const page = parsePage(url.values.page);
  const entityType = parseOption<string>(url.values.entity, CONFIG_ENTITY_TYPES, ALL_TYPES);
  const fromDate = parseDateParam(url.values.from);
  const toDate = parseDateParam(url.values.to);

  const audit = useResource(
    useCallback(
      () =>
        listConfigAudit({
          page,
          size: DEFAULT_PAGE_LIMIT,
          // Omit each key entirely when unset (exactOptionalPropertyTypes: no
          // present-but-undefined), so the wrapper sends no `?entityType=` / dates.
          ...(entityType === ALL_TYPES ? {} : { entityType }),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
        }),
      [page, entityType, fromDate, toDate],
    ),
    [page, entityType, fromDate, toDate],
  );

  const entries = audit.data?.entries ?? [];
  const total = audit.data?.total ?? null;
  const pageLimit = audit.data?.pageLimit ?? DEFAULT_PAGE_LIMIT;

  // Changing a filter restarts at page 1 (the page count differs per filter).
  const changeEntityType = (next: string) => setUrl({ entity: next, page: null });
  const changeFrom = (next: string) => setUrl({ from: next, page: null });
  const changeTo = (next: string) => setUrl({ to: next, page: null });
  const clearAll = () => setUrl({ entity: null, from: null, to: null, page: null });
  const setPage = (next: number) => setUrl({ page: toPageParam(next) });
  const canClear = entityType !== ALL_TYPES || fromDate.length > 0 || toDate.length > 0;

  return (
    <Box>
      <Typography variant="h1" component="h1">
        Config changes
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Every configuration write at this location — connections, plans, credit matrices, caps,
        policies, custom fields and settings — newest first, with who changed what and the
        before&nbsp;&rarr;&nbsp;after. Secret values are shown masked to their last four characters.
      </Typography>

      {/* The shared list toolbar (DNKADM-0430): the Entity type filter, the date range and
          Clear. Server paged, so there is no sort control in Phase 1. */}
      <ListToolbar
        filters={
          <ListSelectFilter
            label="Entity type"
            allLabel="All types"
            value={entityType}
            onChange={changeEntityType}
            options={ENTITY_TYPE_OPTIONS}
          />
        }
        dateRange={{
          from: fromDate,
          to: toDate,
          onFromChange: changeFrom,
          onToChange: changeTo,
        }}
        onClear={clearAll}
        canClear={canClear}
      />

      {audit.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={audit.error}
          what="the config-change log"
          onRetry={audit.refetch}
          forbiddenMessage={roleLimitedMessage('The config-change log', 'OWNER or ADMIN')}
          retryMessage="The config-change log could not be loaded. Please try again."
          preferServerMessage
          sx={{ mt: 2 }}
        />
      ) : (
        <Paper elevation={1} sx={{ px: 3, py: 1 }}>
          {audit.status === 'loading' &&
            Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={`sk-${i}`} />)}

          {audit.status === 'ready' && entries.length === 0 && <EmptyState page={page} />}

          {audit.status === 'ready' &&
            entries.map((entry, i) => (
              <ConfigAuditRow
                key={`row-${page}-${i}`}
                entry={entry}
                last={i === entries.length - 1}
              />
            ))}

          {audit.status === 'ready' && entries.length > 0 && (
            <Pagination
              page={page}
              total={total}
              pageLimit={pageLimit}
              onPrev={() => setPage(Math.max(1, page - 1))}
              onNext={() => setPage(page + 1)}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

/**
 * The Entity type filter's options: every entity type dink-api stores
 * (`CONFIG_ENTITY_TYPES`, labelled in plain words). The selected value is the STORED
 * string and drives the server `?entityType=` param verbatim (sent only when a single
 * type is chosen; "All types" omits it) — the server filter is an exact match
 * (DNKADM-0392).
 */
const ENTITY_TYPE_OPTIONS = CONFIG_ENTITY_TYPES.map((t) => ({ value: t, label: entityTypeLabel(t) }));

function ConfigAuditRow({ entry, last }: { entry: ConfigAuditEntry; last: boolean }) {
  const theme = useTheme();
  const calendarZone = useCalendarZone();
  const meta = [entry.actor, formatTime(entry.timestamp, calendarZone)]
    .filter((s): s is string => typeof s === 'string' && s.length > 0)
    .join(' · ');
  const changes = diffFields(entry);

  return (
    <Box
      sx={{
        py: 1.875,
        borderBottom: last ? 'none' : `1px solid ${inkAlpha(theme, 0.05)}`,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <ActionChip action={entry.action} />
        <EntityChip entityType={entry.entityType} entityKey={entry.entityKey} />
      </Stack>
      {meta.length > 0 && (
        <Typography sx={{ fontSize: 12, color: inkAlpha(theme, 0.5), mt: 0.75 }}>{meta}</Typography>
      )}
      <ChangeList changes={changes} />
    </Box>
  );
}

/** Action chip: create / update / delete (tinted), or a raw unknown action (neutral). */
function ActionChip({ action }: { action: ConfigAuditEntry['action'] }) {
  const maps = buildConfigAuditMaps(useTheme().palette.mode);
  if (action === null) return null;
  const known = (maps.action as Record<string, (typeof maps.action)[keyof typeof maps.action]>)[
    action
  ];
  const label = known?.label ?? action;
  const tint = known ?? maps.actionFallback;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: 11.5,
        fontWeight: 600,
        bgcolor: tint.bg,
        color: tint.fg,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

/** Entity chip: the entity-type label + the entity key (mono), e.g. "Tier credit · gold|bay". */
function EntityChip({
  entityType,
  entityKey,
}: {
  entityType: ConfigAuditEntry['entityType'];
  entityKey: string | null;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const label = entityTypeLabel(entityType);
  return (
    <Chip
      size="small"
      variant="outlined"
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span">{label}</Box>
          {entityKey !== null && (
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: t.textSubtle }}>
              {entityKey}
            </Box>
          )}
        </Box>
      }
      sx={{
        height: 22,
        fontSize: 11.5,
        borderColor: inkAlpha(theme, 0.15),
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

/**
 * The before→after value rows. Each row shows the field name and, depending on
 * the action, the old value, the new value, or `old → new`. Values render as
 * plain mono text — VERBATIM, never an input; a server-masked last-4 secret shows
 * exactly as the server returned it.
 */
function ChangeList({ changes }: { changes: FieldChange[] }) {
  const theme = useTheme();
  if (changes.length === 0) return null;
  return (
    <Stack sx={{ mt: 1, gap: 0.5 }}>
      {changes.map((c) => (
        <Stack
          key={c.key}
          direction="row"
          sx={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 1, fontSize: 12.5 }}
        >
          <Box component="span" sx={{ fontWeight: 500, color: inkAlpha(theme, 0.7), minWidth: 120 }}>
            {c.key}
          </Box>
          <Stack
            direction="row"
            sx={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 0.75, fontFamily: MONO, fontSize: 12 }}
          >
            {c.oldText !== null && (
              <Box
                component="span"
                sx={{
                  color: c.newText !== null ? inkAlpha(theme, 0.5) : inkAlpha(theme, 0.75),
                  textDecoration: c.newText !== null ? 'line-through' : 'none',
                  wordBreak: 'break-word',
                }}
              >
                {c.oldText}
              </Box>
            )}
            {c.oldText !== null && c.newText !== null && (
              <Box component="span" aria-hidden sx={{ color: inkAlpha(theme, 0.4) }}>
                &rarr;
              </Box>
            )}
            {c.newText !== null && (
              <Box component="span" sx={{ color: inkAlpha(theme, 0.85), wordBreak: 'break-word' }}>
                {c.newText}
              </Box>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function SkeletonRow() {
  return (
    <Box sx={{ py: 1.875 }}>
      <Skeleton width={180} height={18} />
      <Skeleton width={140} height={12} sx={{ mt: 0.5 }} />
      <Skeleton width={260} height={12} sx={{ mt: 0.75 }} />
    </Box>
  );
}

function EmptyState({ page }: { page: number }) {
  const theme = useTheme();
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Icon name="manage_history" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No config changes{page > 1 ? ' on this page.' : ' yet.'}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 380, mx: 'auto' }}>
        Connection, plan, credit-matrix, cap, policy, custom-field and settings changes at this
        location will appear here, newest first.
      </Typography>
    </Box>
  );
}

function Pagination({
  page,
  total,
  pageLimit,
  onPrev,
  onNext,
}: {
  page: number;
  total: number | null;
  pageLimit: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  // `total` passes through only when present; without it we cannot know the last
  // page, so "Next" is offered until the end is known (same as the Activity log).
  const t = resolveTokens(useTheme());
  const lastPage = total !== null ? Math.max(1, Math.ceil(total / pageLimit)) : null;
  const atLastPage = lastPage !== null && page >= lastPage;

  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 0.5,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 1}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}
